import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRecipes } from '../hooks/useRecipes';
import { useMealPlans } from '../hooks/useMealPlans';
import { format, startOfWeek, addDays, isToday as checkIsToday } from 'date-fns';
import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import clsx from 'clsx';
import { ProtectedRoute } from '../components/auth';

export const Route = createFileRoute('/app')({
  component: ProtectedMealPlannerPage,
});

function ProtectedMealPlannerPage() {
  return (
    <ProtectedRoute>
      <MealPlannerPage />
    </ProtectedRoute>
  );
}

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snacks'] as const;
const MEAL_CONFIG = {
  breakfast: { emoji: '🌅', label: 'Breakfast', color: 'amber' },
  lunch: { emoji: '☀️', label: 'Lunch', color: 'sky' },
  dinner: { emoji: '🌙', label: 'Dinner', color: 'indigo' },
  snacks: { emoji: '🍿', label: 'Snacks', color: 'pink' },
} as const;

function MealPlannerPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    // Start with today if it's this week
    const today = new Date();
    const weekStartDate = startOfWeek(new Date(), { weekStartsOn: 1 });
    const dayDiff = Math.floor((today.getTime() - weekStartDate.getTime()) / (1000 * 60 * 60 * 24));
    return dayDiff >= 0 && dayDiff < 7 ? dayDiff : 0;
  });
  const [addingMeal, setAddingMeal] = useState<{ date: string; mealType: string } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const queryClient = useQueryClient();
  
  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/login' });
    }
  }, [isLoaded, isSignedIn, navigate]);
  
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const startDate = format(days[0], 'yyyy-MM-dd');
  const endDate = format(days[6], 'yyyy-MM-dd');
  
  // Use Convex hooks
  const { recipes } = useRecipes();
  const { 
    plans: mealPlans, 
    addMeal: addMealConvex, 
    removeMeal: removeMealConvex, 
    updateServings: updateServingsConvex 
  } = useMealPlans(startDate, endDate);
  
  // Add meal mutation
  const addMeal = useMutation({
    mutationFn: async (data: { date: string; recipeId: string; mealType: string; servings?: number }) => {
      return addMealConvex({
        date: data.date,
        recipeId: data.recipeId as any,
        mealType: data.mealType,
        servings: data.servings,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealPlans'] });
      setAddingMeal(null);
    },
  });
  
  // Remove meal mutation
  const removeMeal = useMutation({
    mutationFn: async (mealId: string) => {
      return removeMealConvex(mealId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealPlans'] });
    },
  });

  // Update servings mutation
  const updateServings = useMutation({
    mutationFn: async ({ mealId, servings }: { mealId: string; servings: number }) => {
      return updateServingsConvex({ mealId, servings });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealPlans'] });
    },
  });
  
  // Move meal mutation (for drag and drop / move-to)
  const moveMeal = useMutation({
    mutationFn: async (data: { mealId: string; targetDate: string; targetMealType: string }) => {
      // First get the meal details
      const sourceMeal = mealPlans
        ?.flatMap((p: any) => p.meals || [])
        .find((m: any) => m.id === data.mealId);
      
      if (!sourceMeal) throw new Error('Meal not found');
      
      // Delete from old position
      await removeMealConvex(data.mealId);
      
      // Add to new position
      return addMealConvex({
        date: data.targetDate,
        recipeId: sourceMeal.recipeId,
        mealType: data.targetMealType,
        servings: sourceMeal.servings,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealPlans'] });
    },
  });
  
  const [draggedMeal, setDraggedMeal] = useState<{ id: string; title: string } | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{ date: string; mealType: string } | null>(null);
  const [movingMeal, setMovingMeal] = useState<{ id: string; title: string; currentDate: string; currentMealType: string } | null>(null);
  
  const getMealsForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const plan = mealPlans?.find((p: any) => p.date === dateStr);
    return plan?.meals || [];
  };
  
  const getRecipe = (recipeId: string) => {
    return recipes?.find((r: any) => r.id === recipeId);
  };
  
  const prevWeek = () => {
    setWeekStart(addDays(weekStart, -7));
    setSelectedDayIndex(0);
  };
  const nextWeek = () => {
    setWeekStart(addDays(weekStart, 7));
    setSelectedDayIndex(0);
  };
  
  const prevDay = () => {
    if (selectedDayIndex > 0) {
      setSelectedDayIndex(selectedDayIndex - 1);
    } else {
      setWeekStart(addDays(weekStart, -7));
      setSelectedDayIndex(6);
    }
  };
  
  const nextDay = () => {
    if (selectedDayIndex < 6) {
      setSelectedDayIndex(selectedDayIndex + 1);
    } else {
      setWeekStart(addDays(weekStart, 7));
      setSelectedDayIndex(0);
    }
  };
  
  if (!isLoaded) {
    return <div className="flex items-center justify-center min-h-[50vh]">Loading...</div>;
  }
  
  if (!isSignedIn) {
    return null;
  }

  // Mobile: Single day view
  if (isMobile) {
    const selectedDay = days[selectedDayIndex];
    const dayMeals = getMealsForDay(selectedDay);
    const isToday = checkIsToday(selectedDay);
    
    return (
      <div className="min-h-screen bg-snow">
        {/* Mobile Header */}
        <div className="sticky top-0 z-10 bg-snow/95 backdrop-blur border-b border-mist">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <button 
                onClick={prevDay}
                className="p-2 -ml-2 text-stone hover:text-ink"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="text-center">
                <div className={clsx(
                  'text-lg font-semibold',
                  isToday ? 'text-coral-600' : 'text-ink'
                )}>
                  {isToday ? 'Today' : format(selectedDay, 'EEEE')}
                </div>
                <div className="text-sm text-stone">
                  {format(selectedDay, 'MMMM d, yyyy')}
                </div>
              </div>
              
              <button 
                onClick={nextDay}
                className="p-2 -mr-2 text-stone hover:text-ink"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Day picker dots */}
            <div className="flex justify-center gap-1.5 mt-2">
              {days.map((day, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedDayIndex(idx)}
                  className={clsx(
                    'w-2 h-2 rounded-full transition-all',
                    idx === selectedDayIndex 
                      ? 'bg-coral-500 w-4' 
                      : checkIsToday(day) 
                        ? 'bg-coral-300' 
                        : 'bg-sand'
                  )}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Meals for the day */}
        <div className="p-4 space-y-4">
          {MEAL_TYPES.map((mealType) => {
            const meals = dayMeals.filter((m: any) => m.mealType === mealType);
            const config = MEAL_CONFIG[mealType];

            return (
              <div
                key={mealType}
                className={clsx(
                  "bg-white rounded-xl border overflow-hidden",
                  dragOverSlot?.date === format(selectedDay, 'yyyy-MM-dd') &&
                    dragOverSlot?.mealType === mealType
                    ? "border-coral-400 bg-coral-50"
                    : "border-mist"
                )}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverSlot({
                    date: format(selectedDay, 'yyyy-MM-dd'),
                    mealType,
                  });
                }}
                onDragLeave={() => setDragOverSlot(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedMeal) {
                    moveMeal.mutate({
                      mealId: draggedMeal.id,
                      targetDate: format(selectedDay, 'yyyy-MM-dd'),
                      targetMealType: mealType,
                    });
                    setDragOverSlot(null);
                  }
                }}
              >
                <div className="px-4 py-2 bg-sand/30 border-b border-mist flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{config.emoji}</span>
                    <span className="font-medium text-ink">{config.label}</span>
                    {meals.length > 0 && (
                      <span className="text-sm text-stone/60">({meals.length})</span>
                    )}
                  </div>
                  {meals.length > 0 && (
                    <button
                      onClick={() => setAddingMeal({
                        date: format(selectedDay, 'yyyy-MM-dd'),
                        mealType
                      })}
                      className="text-sm text-coral-600 hover:text-coral-700 font-medium"
                    >
                      + Add
                    </button>
                  )}
                </div>
                
                {meals.length > 0 ? (
                  <div className="divide-y divide-mist">
                    {meals.map((meal: any) => {
                      return (
                        <div 
                          key={meal.id} 
                          className="p-4"
                        >
                          <div className="flex gap-3">
                            {/* Move button - replaces drag on mobile */}
                            <button
                              className="flex-shrink-0 w-8 flex flex-col items-center justify-center gap-1 text-stone/40 active:text-coral-500 select-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMovingMeal({
                                  id: meal.id,
                                  title: meal.title || "Recipe",
                                  currentDate: format(selectedDay, 'yyyy-MM-dd'),
                                  currentMealType: mealType,
                                });
                              }}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                              </svg>
                              <span className="text-[10px]">Move</span>
                            </button>
                            
                            <Link
                              to="/recipes/$recipeId"
                              params={{ recipeId: meal.recipeId }}
                              className="flex-1 flex gap-3 hover:bg-sand/30 transition-colors -m-4 p-4 ml-0"
                            >
                              {meal.imageUrl ? (
                                <img 
                                  src={meal.imageUrl} 
                                  alt={meal.title}
                                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-sand flex items-center justify-center flex-shrink-0">
                                  <span className="text-2xl">🍽️</span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-ink">{meal.title || "Recipe"}</h3>
                              </div>
                            </Link>
                            
                            {/* Servings selector - outside Link so it's clickable */}
                            <div className="flex items-center gap-1 self-center">
                              <span className="text-xs text-coral-600 font-medium">🍽️</span>
                              <select
                                value={meal.servings || 2}
                                onChange={(e) => {
                                  updateServings.mutate({
                                    mealId: meal.id,
                                    servings: parseInt(e.target.value),
                                  });
                                }}
                                className="bg-transparent text-xs text-coral-600 font-medium cursor-pointer focus:outline-none"
                              >
                                {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                                  <option key={n} value={n}>
                                    {n}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          {/* Quick remove */}
                          <button
                            onClick={() => removeMeal.mutate(meal.id)}
                            className="mt-3 w-full py-2 px-4 text-sm font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors"
                          >
                            ✕ Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingMeal({ 
                      date: format(selectedDay, 'yyyy-MM-dd'), 
                      mealType 
                    })}
                    className="w-full p-6 text-center text-stone hover:text-coral-600 hover:bg-coral-50 transition-colors"
                  >
                    <div className="text-3xl mb-2">+</div>
                    <div className="text-sm">Add {config.label.toLowerCase()}</div>
                  </button>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Add Meal Modal */}
        {addingMeal && <AddMealModal 
          addingMeal={addingMeal} 
          recipes={recipes} 
          addMeal={addMeal} 
          onClose={() => setAddingMeal(null)} 
        />}
        
        {/* Move Meal Modal (mobile) */}
        {movingMeal && (
          <MoveMealModal
            meal={movingMeal}
            days={days}
            onMove={(targetDate, targetMealType) => {
              moveMeal.mutate({
                mealId: movingMeal.id,
                targetDate,
                targetMealType,
              });
              setMovingMeal(null);
            }}
            onClose={() => setMovingMeal(null)}
          />
        )}
      </div>
    );
  }
  
  // Desktop: Week view
  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">
            Meal Plan
          </h1>
          <p className="text-stone text-sm mt-1">
            {format(days[0], 'MMM d')} - {format(days[6], 'MMM d, yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevWeek} className="btn btn-secondary">
            ← Prev
          </button>
          <button onClick={nextWeek} className="btn btn-secondary">
            Next →
          </button>
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-3">
        {days.map((day) => {
          const isToday = checkIsToday(day);
          const dayMeals = getMealsForDay(day);
          
          return (
            <div
              key={day.toISOString()}
              className={clsx(
                'min-h-[200px] rounded-lg border p-3',
                isToday 
                  ? 'border-coral-300 bg-coral-50/30' 
                  : 'border-mist bg-white'
              )}
            >
              {/* Day Header */}
              <div className={clsx(
                'text-center pb-2 mb-2 border-b',
                isToday ? 'border-coral-200' : 'border-mist'
              )}>
                <div className={clsx(
                  'text-xs font-medium uppercase tracking-wide',
                  isToday ? 'text-coral-600' : 'text-stone'
                )}>
                  {format(day, 'EEE')}
                </div>
                <div className={clsx(
                  'text-lg font-semibold',
                  isToday ? 'text-coral-700' : 'text-ink'
                )}>
                  {format(day, 'd')}
                </div>
              </div>
              
              {/* Meals */}
              <div className="space-y-2">
                {MEAL_TYPES.map((mealType) => {
                  const meals = dayMeals.filter((m: any) => m.mealType === mealType);
                  const config = MEAL_CONFIG[mealType];

                  return (
                    <div key={mealType} className="space-y-1">
                      <div className="flex items-center gap-1 text-xs text-stone">
                        <span>{config.emoji}</span>
                        <span className="capitalize">{config.label}</span>
                        {meals.length > 0 && (
                          <span className="text-stone/60">({meals.length})</span>
                        )}
                      </div>

                      {meals.map((meal: any) => (
                        <div
                          key={meal.id}
                          className={clsx(
                            "group relative rounded cursor-move",
                            draggedMeal?.id === meal.id && "opacity-50"
                          )}
                          draggable
                          onDragStart={() =>
                            setDraggedMeal({
                              id: meal.id,
                              title:
                                getRecipe(meal.recipeId)?.title || "Recipe",
                            })
                          }
                          onDragEnd={() => {
                            setDraggedMeal(null);
                            setDragOverSlot(null);
                          }}
                        >
                          <Link
                            to="/recipes/$recipeId"
                            params={{ recipeId: meal.recipeId }}
                            className="block text-xs p-2 rounded bg-sand hover:bg-coral-100 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="font-medium text-ink truncate pr-6">
                              {getRecipe(meal.recipeId)?.title || "Recipe"}
                            </div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-stone">🍽️</span>
                              <select
                                value={meal.servings || 2}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  updateServings.mutate({
                                    mealId: meal.id,
                                    servings: parseInt(e.target.value),
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-transparent text-stone text-xs cursor-pointer hover:text-coral-600 focus:outline-none focus:text-coral-600"
                              >
                                {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                                  <option key={n} value={n}>
                                    {n}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </Link>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMeal.mutate(meal.id);
                            }}
                            className="absolute top-1/2 -translate-y-1/2 right-1 w-6 h-6 rounded-full bg-rose-100 hover:bg-rose-200 text-rose-600 flex items-center justify-center text-sm font-medium transition-colors"
                            title="Remove from plan"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {/* Add button - always shown for all meal types */}
                      <div
                        className={clsx(
                          "w-full text-xs p-2 rounded border border-dashed transition-colors text-left",
                          dragOverSlot?.date === format(day, 'yyyy-MM-dd') &&
                            dragOverSlot?.mealType === mealType
                            ? "border-coral-400 bg-coral-50 text-coral-700"
                            : "border-mist text-stone hover:border-coral-300 hover:text-coral-600"
                        )}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOverSlot({
                            date: format(day, 'yyyy-MM-dd'),
                            mealType,
                          });
                        }}
                        onDragLeave={() => setDragOverSlot(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (draggedMeal) {
                            moveMeal.mutate({
                              mealId: draggedMeal.id,
                              targetDate: format(day, 'yyyy-MM-dd'),
                              targetMealType: mealType,
                            });
                            setDragOverSlot(null);
                          }
                        }}
                        onClick={() =>
                          setAddingMeal({
                            date: format(day, 'yyyy-MM-dd'),
                            mealType,
                          })
                        }
                      >
                        {meals.length === 0 ? '+ Add' : '+ Add more'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Add Meal Modal */}
      {addingMeal && <AddMealModal 
        addingMeal={addingMeal} 
        recipes={recipes} 
        addMeal={addMeal} 
        onClose={() => setAddingMeal(null)} 
      />}
    </div>
  );
}

// Shared modal component
function AddMealModal({ 
  addingMeal, 
  recipes, 
  addMeal, 
  onClose 
}: { 
  addingMeal: { date: string; mealType: string };
  recipes: any[];
  addMeal: any;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-mist flex items-center justify-between">
          <h2 className="font-semibold text-ink">
            Add {MEAL_CONFIG[addingMeal.mealType as keyof typeof MEAL_CONFIG].label}
          </h2>
          <button 
            onClick={onClose}
            className="text-stone hover:text-ink"
          >
            ✕
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto flex-1">
          {!recipes?.length ? (
            <div className="text-center py-8">
              <p className="text-stone mb-4">No recipes yet</p>
              <Link to="/recipes" className="btn btn-primary">
                Add Your First Recipe
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recipes.map((recipe: any) => (
                <button
                  key={recipe.id}
                  onClick={() => addMeal.mutate({
                    date: addingMeal.date,
                    recipeId: recipe.id,
                    mealType: addingMeal.mealType,
                    servings: recipe.servings || 2,
                  })}
                  className="w-full text-left p-3 rounded-lg border border-mist hover:border-coral-300 hover:bg-coral-50 transition-all flex gap-3"
                >
                  {recipe.imageUrl && (
                    <img 
                      src={recipe.imageUrl} 
                      alt="" 
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink">{recipe.title}</div>
                    {recipe.description && (
                      <div className="text-sm text-stone line-clamp-1">
                        {recipe.description}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Move meal modal for mobile (replaces drag and drop)
function MoveMealModal({
  meal,
  days,
  onMove,
  onClose,
}: {
  meal: { id: string; title: string; currentDate: string; currentMealType: string };
  days: Date[];
  onMove: (targetDate: string, targetMealType: string) => void;
  onClose: () => void;
}) {
  const [selectedDay, setSelectedDay] = useState(meal.currentDate);
  const [selectedMealType, setSelectedMealType] = useState(meal.currentMealType);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-sm max-h-[70vh] flex flex-col">
        <div className="p-4 border-b border-mist flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-ink">Move Recipe</h2>
            <p className="text-sm text-stone truncate">{meal.title}</p>
          </div>
          <button onClick={onClose} className="text-stone hover:text-ink p-1">✕</button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Day picker */}
          <div>
            <label className="text-sm font-medium text-ink mb-2 block">Day</label>
            <div className="grid grid-cols-7 gap-1">
              {days.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const isSelected = dateStr === selectedDay;
                return (
                  <button
                    key={dateStr}
                    onClick={() => setSelectedDay(dateStr)}
                    className={clsx(
                      'flex flex-col items-center py-2 rounded-lg text-xs transition-colors',
                      isSelected
                        ? 'bg-coral-500 text-white'
                        : 'bg-sand/50 text-stone hover:bg-sand'
                    )}
                  >
                    <span className="font-medium">{format(day, 'EEE')}</span>
                    <span>{format(day, 'd')}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Meal type picker */}
          <div>
            <label className="text-sm font-medium text-ink mb-2 block">Meal</label>
            <div className="grid grid-cols-2 gap-2">
              {MEAL_TYPES.map((mt) => {
                const config = MEAL_CONFIG[mt];
                const isSelected = mt === selectedMealType;
                return (
                  <button
                    key={mt}
                    onClick={() => setSelectedMealType(mt)}
                    className={clsx(
                      'flex items-center gap-2 p-3 rounded-lg text-sm font-medium transition-colors',
                      isSelected
                        ? 'bg-coral-500 text-white'
                        : 'bg-sand/50 text-stone hover:bg-sand'
                    )}
                  >
                    <span>{config.emoji}</span>
                    <span>{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-mist flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 px-4 rounded-lg border border-mist text-stone font-medium">
            Cancel
          </button>
          <button
            onClick={() => onMove(selectedDay, selectedMealType)}
            disabled={selectedDay === meal.currentDate && selectedMealType === meal.currentMealType}
            className="flex-1 py-2.5 px-4 rounded-lg bg-coral-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Move Here
          </button>
        </div>
      </div>
    </div>
  );
}
